import React, { memo, useCallback, useState, createElement } from "react";
import { View, StyleSheet } from "react-native";
import { useUnistyles } from "react-native-unistyles";

type WebInputType = "date" | "time";

interface WebPickerInputProps {
  /** HTML input type */
  type: WebInputType;
  /** Current value in HTML input format (YYYY-MM-DD for date, HH:MM for time) */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Error message to display */
  error?: string;
  /** Size variant */
  size?: "small" | "medium" | "large";
  /** Minimum value (YYYY-MM-DD for date) */
  min?: string;
  /** Maximum value (YYYY-MM-DD for date) */
  max?: string;
}

const SIZE_MAP: Record<"small" | "medium" | "large", number> = {
  small: 20,
  medium: 36,
  large: 44,
};

/**
 * Web-specific input component for date/time pickers.
 * Uses native HTML date/time inputs for better UX on web.
 *
 * @example
 * ```tsx
 * <WebPickerInput
 *   type="date"
 *   value="2024-01-15"
 *   onChange={(v) => console.log(v)}
 *   min="2000-01-01"
 *   max="2030-01-01"
 * />
 * ```
 */
function WebPickerInputInner({ type, value, onChange, error, size = "medium", min, max }: WebPickerInputProps) {
  const { theme } = useUnistyles();
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (newValue) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  return (
    <View style={styles.container}>
      {createElement("input", {
        type,
        value,
        onChange: handleChange,
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        style: {
          height: SIZE_MAP[size],
          padding: "8px 12px",
          border:
            error ?
              `1px solid ${theme.colors.status.error}`
            : `1px solid ${isFocused ? theme.colors.navy[500] : theme.colors.grey[600]}`,
          borderRadius: "6px",
          fontSize: "14px",
          backgroundColor: theme.colors.background,
          outline: "none",
          color: theme.colors.text,
          colorScheme: theme.currentTheme === "dark" ? "dark" : "light",
        },
        ...(min && { min }),
        ...(max && { max }),
      })}
      {error && (
        <View style={styles.errorContainer}>
          {createElement(
            "span",
            {
              style: {
                color: theme.colors.status.error,
                fontSize: "12px",
                marginTop: "4px",
              },
            },
            error,
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  errorContainer: {
    marginTop: 4,
  },
});

export const WebPickerInput = memo(WebPickerInputInner);
